/**
 * A spot the reader holds, as a conversation (owner, 2026-08-27).
 *
 * THE THREAD IS THE LINE THEY WROTE. Registering interest used to end on a
 * screen — "You're #14", the asking tools, and a way out — and the line
 * disappeared into it: the next time anybody wanted to see what they had asked
 * for, there was nowhere to look. So the registration became a row in the
 * console like everything else somebody is waiting on, and this is what opens
 * when it is picked: their own words, in their own bubble, with the one verb
 * that makes sense over a sentence you wrote weeks ago — Edit.
 *
 * WHAT IT IS NOT, AND THE SHAPE SAYS SO. There is no composer, because there is
 * nobody to talk to: the handle has no door and no doorman (FR-G9.5's mirror
 * image). Nothing here is assessed, nothing is scored, no money moves, and NO
 * FIGURE IS SHOWN AT ALL. There was one — "You're #14", the position the
 * registrant took on arrival — and ruling R-0916-3 retired it: a queue number
 * tells the person holding it how many got there first, which is the count ToU
 * 2.2 §5A.5 says nobody but the owner may learn (H2, H3, H9).
 *
 * THE ASKING LIVES HERE NOW (FR-G6). The registrant is the acquisition channel
 * and the tools are theirs to use, but they were handed over once, on the
 * screen this thread replaces. A button that fetches them is both honest about
 * what it does — `POST /advocate` marks that somebody went to ask — and
 * reachable on the fiftieth day as well as the first.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  advocateWaiting,
  registerWaiting,
  updateWaitingLine,
  withdrawWaiting,
  type AdvocacyAssets,
  type MyWaiting,
} from "../../lib/api";
import { myWaitingQuery, qk } from "../../lib/queries";
import { SPOT_STOPPED_PREVIEW, type Conversation } from "../../lib/conversations";
import { ChatShell, SenderBubble, SystemRow, ThreadCard } from "../knock/chat";
import { Button, LinkButton, QuietButton } from "../ui/button";
import { NobFace } from "../shell/mascot";
import { REGISTER_FAILED_COPY, REGISTER_LIMIT_COPY, REGISTER_REFUSED_COPY } from "./waiting-area";

export const SPOT_TITLE = "No Nob here yet.";
/** What a spot IS, said once, in the thread that holds one. */
export const SPOT_LEDE =
  "You are holding a spot at this handle. It is not a knock — nothing was assessed and " +
  "nothing was charged. The day they set up their Nob, you knock for real and they see " +
  "you were waiting.";
export const SPOT_LINE_LABEL = "Your line";
export const EDIT_LINE = "Edit";
export const SAVE_LINE = "Save";
export const CANCEL_EDIT = "Cancel";
export const EDIT_FIELD_LABEL = "What do you want from them?";
export const EDIT_FAILED = "That did not save. Try that again.";
export const EDIT_REFUSED = "That line is not going to work here. Say what you actually want.";
export const EDIT_GONE = "That spot is not held any more.";
export const STOP_WAITING = "Stop waiting";
export const WITHDRAW_FAILED = "Nob is not answering. Try that again.";
/**
 * The way back from a stop (F-27). Withdraw-then-re-register is legal — the
 * one-per-person index is scoped to active/converted — so the stopped thread's
 * one action re-registers the same line through the ordinary POST.
 */
export const HOLD_AGAIN = "Hold the spot again";

/** FR-G6, in the thread: the ask is the registrant's to make, from their account. */
export const ASK_THEM = "Ask them to open their door";
export const ASK_LEDE =
  "We will not email them on your behalf. The fastest way to get this door opened is to " +
  "ask them yourself.";
export const ASK_FAILED = "Nob could not put that together. Try that again.";
export const POST_IT = "Post it";
export const COPY_LINK = "Copy the link";
export const COPIED = "Copied.";

export function SpotThread({ conv }: { conv: Conversation }) {
  const client = useQueryClient();
  const { data: mine = [] } = useQuery({ ...myWaitingQuery(), retry: false });
  const spot = mine.find((r) => r.handle.toLowerCase() === conv.handle.toLowerCase());
  /**
   * The row is the truth and the conversation is the fallback. They say the
   * same thing — the row is what the conversation was mapped from — but the
   * list can be a beat behind an edit made in this very pane, and the bubble
   * must never show the sentence somebody just replaced. The fallback reads
   * `preview` only while it is the LINE: a withdrawn row's preview is the
   * stopped note, which is not a sentence anybody wrote.
   */
  const stopped = spot?.state === "withdrawn";
  const line = spot?.line ?? (conv.preview === SPOT_STOPPED_PREVIEW ? "" : conv.preview);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(line);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = () => {
    setDraft(line);
    setError(null);
    setEditing(true);
  };

  const save = () => {
    const text = draft.trim();
    if (text.length < 3 || busy) return;
    setBusy(true);
    setError(null);
    void updateWaitingLine(conv.handle, text).then((result) => {
      setBusy(false);
      if (!result.ok) {
        setError(
          result.code === "REGISTRATION_REFUSED"
            ? EDIT_REFUSED
            : result.code === "REGISTRATION_NOT_FOUND"
              ? EDIT_GONE
              : EDIT_FAILED,
        );
        return;
      }
      // The server's own words back, not the ones that were typed: it trims and
      // collapses whitespace, and a bubble showing the untrimmed version would
      // disagree with the row the next read hands back.
      client.setQueryData(qk.myWaiting(), (rows: typeof mine | undefined) =>
        rows?.map((r) =>
          r.handle.toLowerCase() === conv.handle.toLowerCase() ? { ...r, line: result.line } : r,
        ),
      );
      void client.invalidateQueries({ queryKey: qk.myWaiting() });
      setEditing(false);
    });
  };

  /** One writer for the row this pane just changed, then the re-read. */
  const settle = (map: (r: MyWaiting) => MyWaiting) => {
    client.setQueryData(qk.myWaiting(), (rows: typeof mine | undefined) =>
      rows?.map((r) => (r.handle.toLowerCase() === conv.handle.toLowerCase() ? map(r) : r)),
    );
    void client.invalidateQueries({ queryKey: qk.myWaiting() });
  };

  /**
   * Stopping releases the SPOT AND KEEPS THE CONVERSATION (owner, F-27). The
   * thread does not close and nothing navigates: the row reads as stopped, the
   * line stands as history, and the one action left is to hold the spot again.
   * Closing the pane here was how the conversation used to vanish from the
   * console the moment somebody stopped.
   */
  const stop = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void withdrawWaiting(conv.handle).then((withdrawn) => {
      setBusy(false);
      if (!withdrawn) {
        setError(WITHDRAW_FAILED);
        return;
      }
      settle((r) => ({ ...r, state: "withdrawn" }));
    });
  };

  /**
   * The way back (F-27): the SAME POST an arrival makes, with the line that
   * already stands. Legal because the one-per-person index is scoped to
   * active/converted — a withdrawn row does not hold the slot. The refusals are
   * the register path's own, in its own words; one attempt per press, and a
   * refusal leaves the thread stopped with the line untouched.
   */
  const holdAgain = () => {
    const text = line.trim();
    if (busy || text.length < 3) return;
    setBusy(true);
    setError(null);
    void registerWaiting(conv.handle, text).then((result) => {
      setBusy(false);
      if (result.ok) {
        settle((r) => ({ ...r, state: "active", line: text }));
        return;
      }
      // Held after all — two panes, or a beat-behind list. The line that
      // STANDS is the one the server sent back.
      if (result.code === "ALREADY_REGISTERED") {
        settle((r) => ({ ...r, state: "active", line: result.line ?? r.line }));
        return;
      }
      setError(
        result.code === "REGISTRATION_REFUSED"
          ? REGISTER_REFUSED_COPY
          : result.code === "REGISTRATION_LIMIT"
            ? REGISTER_LIMIT_COPY
            : REGISTER_FAILED_COPY,
      );
    });
  };

  return (
    <ChatShell fill header={null}>
      {/* The plate. No name, no picture, no bio and no count of other people:
          nobody at this handle has signed up, so there is no profile of them to
          print (H2). The handle is the whole of what is known. */}
      <ThreadCard label={`@${conv.handle}`}>
        <div className="flex items-start gap-3">
          <NobFace mood="bored" size="md" />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold tracking-tight">{SPOT_TITLE}</p>
            <p className="truncate font-mono text-xs text-faint">@{conv.handle}</p>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-text-soft">{SPOT_LEDE}</p>
      </ThreadCard>

      {/* STOPPED (F-27): the line stands as history — nothing to edit, nothing
          to stop — and the one verb left is the way back in. No asking tools
          either: somebody who stopped waiting is not this door's advocate. */}
      {stopped ? (
        <>
          {line.trim() && <SenderBubble>{line}</SenderBubble>}
          <SystemRow>{SPOT_STOPPED_PREVIEW}</SystemRow>
          <div className="flex justify-center">
            <Button type="button" className="h-9 px-4" disabled={busy} onClick={holdAgain}>
              {HOLD_AGAIN}
            </Button>
          </div>
          {error && (
            <p role="alert" className="px-6 text-center text-xs leading-relaxed text-muted">
              {error}
            </p>
          )}
        </>
      ) : (
        <>
          {/* Their own words, on their own side of the thread. It is the one
          message in this conversation, because it is the only thing anybody
          has said. */}
          {editing ? (
            <ThreadCard label={SPOT_LINE_LABEL}>
              <label htmlFor="spot-line" className="sr-only">
                {EDIT_FIELD_LABEL}
              </label>
              <textarea
                id="spot-line"
                rows={2}
                maxLength={280}
                autoFocus
                value={draft}
                aria-label={EDIT_FIELD_LABEL}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full resize-none rounded-xl border border-border bg-surface/60 px-3 py-2
                       text-[15px] leading-relaxed text-text placeholder:text-fainter
                       focus-visible:border-border-loud focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-focus/40"
              />
              <div className="mt-3 flex items-center gap-3">
                <Button
                  type="button"
                  className="h-9 px-4"
                  disabled={busy || draft.trim().length < 3}
                  onClick={save}
                >
                  {SAVE_LINE}
                </Button>
                <QuietButton type="button" onClick={() => setEditing(false)}>
                  {CANCEL_EDIT}
                </QuietButton>
              </div>
            </ThreadCard>
          ) : (
            <>
              <SenderBubble>{line}</SenderBubble>
              <div className="flex justify-end gap-4 pr-1">
                <LinkButton type="button" onClick={open}>
                  {EDIT_LINE}
                </LinkButton>
                <LinkButton type="button" onClick={stop}>
                  {STOP_WAITING}
                </LinkButton>
              </div>
            </>
          )}

          {error && (
            <p role="alert" className="px-6 text-center text-xs leading-relaxed text-muted">
              {error}
            </p>
          )}

          <SystemRow>{ASK_LEDE}</SystemRow>
          <Advocacy handle={conv.handle} />
        </>
      )}
    </ChatShell>
  );
}

/**
 * The asking tools, fetched when somebody goes to ask (FR-G6).
 *
 * NOT ON MOUNT, and that is the endpoint's own semantics rather than a
 * performance choice: reading them marks that this registrant went to advocate,
 * and a mark that fired every time a thread was opened would be a number about
 * nothing.
 */
function Advocacy({ handle }: { handle: string }) {
  const [assets, setAssets] = useState<AdvocacyAssets | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!assets) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Button
          type="button"
          className="h-9 px-4"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setFailed(false);
            void advocateWaiting(handle).then((got) => {
              setBusy(false);
              if (got) setAssets(got);
              else setFailed(true);
            });
          }}
        >
          {ASK_THEM}
        </Button>
        {failed && (
          <p role="alert" className="text-xs text-muted">
            {ASK_FAILED}
          </p>
        )}
      </div>
    );
  }

  return (
    <ThreadCard label={ASK_THEM}>
      <p className="text-sm leading-relaxed text-text-soft">{assets.nudge}</p>
      <p className="mt-3 rounded-lg border border-border bg-bg/60 p-3 font-mono text-[13px] leading-relaxed text-text-soft">
        {assets.tweet}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a
          href={assets.tweet_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-9 items-center rounded-lg bg-accent px-4 text-sm font-medium
                     text-accent-fg hover:bg-accent-soft"
        >
          {POST_IT}
        </a>
        <QuietButton
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(assets.url);
            setCopied(true);
          }}
        >
          {copied ? COPIED : COPY_LINK}
        </QuietButton>
      </div>
    </ThreadCard>
  );
}
